import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    bio: '',
    age_range: '',
    height: '',
    weight: '',
    skills: [],
    representation_type: '',
    demo_reel_url: '',
    resume_url: '',
    headshot_url: '',
    instagram: '',
    imdb: '',
    website: ''
  });
  const [skillInput, setSkillInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, skillInput.trim()] });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/talents/register`, formData);
      toast.success('Registration submitted! You will be notified once approved.');
      // Reset form
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        bio: '',
        age_range: '',
        height: '',
        weight: '',
        skills: [],
        representation_type: '',
        demo_reel_url: '',
        resume_url: '',
        headshot_url: '',
        instagram: '',
        imdb: '',
        website: ''
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header />
      
      <div className="page-header" data-testid="register-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="section-subtitle">Join Our Roster</p>
            <h1 className="section-title">Talent Registration</h1>
            <p className="section-description">
              Submit your profile for consideration. Our team will review your application 
              and get back to you within 5-7 business days.
            </p>
          </motion.div>
        </div>
      </div>

      <section className="section section-dark" data-testid="register-section">
        <div className="container">
          <motion.div
            className="register-form-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="contact-form-container" data-testid="register-form">
              
              {/* Personal Information */}
              <div className="form-section">
                <h3 className="form-section-title">Personal Information</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Password *</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="form-input"
                      required
                      minLength={6}
                      data-testid="register-password-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="register-phone-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Bio / About You</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="Tell us about yourself, your experience, and what makes you unique..."
                    data-testid="register-bio-input"
                  />
                </div>
              </div>

              {/* Physical Attributes */}
              <div className="form-section">
                <h3 className="form-section-title">Physical Attributes</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Age Range</label>
                    <select
                      name="age_range"
                      value={formData.age_range}
                      onChange={handleChange}
                      className="form-select"
                      data-testid="register-age-range"
                    >
                      <option value="">Select...</option>
                      <option value="18-25">18-25</option>
                      <option value="25-35">25-35</option>
                      <option value="35-45">35-45</option>
                      <option value="45-55">45-55</option>
                      <option value="55+">55+</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Representation Type</label>
                    <select
                      name="representation_type"
                      value={formData.representation_type}
                      onChange={handleChange}
                      className="form-select"
                      data-testid="register-representation-type"
                    >
                      <option value="">Select...</option>
                      <option value="Actor">Actor</option>
                      <option value="Model">Model</option>
                      <option value="Voice Artist">Voice Artist</option>
                      <option value="Performer">Performer</option>
                      <option value="Multi-Talent">Multi-Talent</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Height</label>
                    <input
                      type="text"
                      name="height"
                      value={formData.height}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="e.g., 5'10&quot;"
                      data-testid="register-height-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Weight</label>
                    <input
                      type="text"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="e.g., 160 lbs"
                      data-testid="register-weight-input"
                    />
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div className="form-section">
                <h3 className="form-section-title">Skills & Specialties</h3>
                
                <div className="form-group">
                  <label className="form-label">Add Skills</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      className="form-input"
                      placeholder="e.g., Stage Combat, Accents, Dancing..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                      data-testid="register-skill-input"
                    />
                    <button 
                      type="button" 
                      onClick={addSkill} 
                      className="btn btn-secondary"
                      data-testid="add-skill-btn"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                  {formData.skills.length > 0 && (
                    <div className="skills-container" data-testid="skills-list">
                      {formData.skills.map((skill, idx) => (
                        <span key={idx} className="skill-item">
                          {skill}
                          <button 
                            type="button" 
                            onClick={() => removeSkill(skill)} 
                            className="skill-remove"
                            data-testid={`remove-skill-${idx}`}
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Media & Links */}
              <div className="form-section">
                <h3 className="form-section-title">Media & Links</h3>
                
                <div className="form-group">
                  <label className="form-label">Headshot URL</label>
                  <input
                    type="url"
                    name="headshot_url"
                    value={formData.headshot_url}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="https://..."
                    data-testid="register-headshot-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Demo Reel URL</label>
                  <input
                    type="url"
                    name="demo_reel_url"
                    value={formData.demo_reel_url}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="YouTube, Vimeo, etc."
                    data-testid="register-demoreel-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Resume URL</label>
                  <input
                    type="url"
                    name="resume_url"
                    value={formData.resume_url}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Link to your resume/CV"
                    data-testid="register-resume-input"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Instagram</label>
                    <input
                      type="text"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="@username or full URL"
                      data-testid="register-instagram-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">IMDb</label>
                    <input
                      type="url"
                      name="imdb"
                      value={formData.imdb}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="IMDb profile URL"
                      data-testid="register-imdb-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Personal Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="https://..."
                    data-testid="register-website-input"
                  />
                </div>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary" 
                style={{ width: '100%', marginTop: '1rem' }}
                disabled={submitting}
                data-testid="register-submit-button"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>

              <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
                By submitting this form, you agree to our review process. 
                Approved profiles will be displayed on our public roster.
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
